<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ContractFormFields, { type InitialEntities } from './ContractFormFields.vue';
import ContractRecipeSection from './ContractRecipeSection.vue';
import {
  contractToFormData,
  validateContractForm,
  type ContractFormData,
} from 'src/composables/useContractManagement';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';
import type { FetchOrganisationResponseLite } from 'src/api/models/fetchOrganisationResponseLite';
import type { FetchUnitResponseLite } from 'src/api/models/fetchUnitResponseLite';
import type { FetchAddressResponse } from 'src/api/models/fetchAddressResponse';
import type { NestedOrganisationContactPerson } from 'src/api/models/nestedOrganisationContactPerson';
import type { FetchRecipeResponseLite } from 'src/api/models/fetchRecipeResponseLite';

const props = defineProps<{
  contract: FetchContractResponse;
  saving?: boolean;
  fullEdit?: boolean;
  searchOrganisationsFn: (query: string, page: number) => Promise<{ data: FetchOrganisationResponseLite[]; page: number; total_pages: number }>;
  searchUnitsFn: (orgId: string, query: string) => Promise<{ data: FetchUnitResponseLite[]; page: number; total_pages: number }>;
  searchBranchesFn: (orgId: string | null, query: string, page: number) => Promise<{ data: FetchAddressResponse[]; page: number; total_pages: number }>;
  searchContactPersonsFn: (orgId: string, query: string, page: number) => Promise<{ data: NestedOrganisationContactPerson[]; page: number; total_pages: number }>;
  searchRecipesFn: (query: string, page: number) => Promise<{ data: FetchRecipeResponseLite[]; page: number; total_pages: number }>;
}>();

const emit = defineEmits<{
  save: [data: ContractFormData];
  cancel: [];
  'dirty-change': [dirty: boolean];
}>();

const initialFormData = ref<ContractFormData>(contractToFormData(props.contract));
const formData = ref<ContractFormData>(JSON.parse(JSON.stringify(initialFormData.value)));
const submitted = ref(false);

const initialEntities = computed<InitialEntities>(() => ({
  customer: props.contract.contract_holder_detail as FetchOrganisationResponseLite | null,
  supplier: props.contract.operator_detail as FetchOrganisationResponseLite | null,
  location: (props.contract.operator_branch ?? null) as FetchAddressResponse | null,
  contact: props.contract.contract_holder_contact_person as NestedOrganisationContactPerson | null,
  unit: props.contract.unit as FetchUnitResponseLite | null,
}));

watch(
  () => props.contract,
  (next) => {
    initialFormData.value = contractToFormData(next);
    formData.value = JSON.parse(JSON.stringify(initialFormData.value));
    submitted.value = false;
  },
);

const validationErrors = computed(() => {
  if (!submitted.value) return {};
  return validateContractForm(formData.value);
});

const isValid = computed(() => Object.keys(validationErrors.value).length === 0);

const isDirty = computed(
  () => JSON.stringify(formData.value) !== JSON.stringify(initialFormData.value),
);

watch(isDirty, (val) => emit('dirty-change', val), { immediate: true });

function onSave() {
  submitted.value = true;
  if (!isValid.value) return;
  emit('save', { ...formData.value });
}

function onCancel() {
  formData.value = JSON.parse(JSON.stringify(initialFormData.value));
  submitted.value = false;
  emit('cancel');
}

defineExpose({ isDirty });
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="contract-detail-edit-form">
    <ContractFormFields
      v-model="formData"
      :readonly-org="!fullEdit"
      :initial-entities="initialEntities"
      :errors="validationErrors"
      :search-organisations-fn="searchOrganisationsFn"
      :search-units-fn="searchUnitsFn"
      :search-branches-fn="searchBranchesFn"
      :search-contact-persons-fn="searchContactPersonsFn"
    />

    <Divider />

    <ContractRecipeSection
      v-model="formData.recipes"
      :lock-recipes="!fullEdit"
      :submitted="submitted"
      :search-recipes-fn="searchRecipesFn"
    />

    <div v-if="submitted && !isValid" class="text-red-500 text-sm">
      Vul alle verplichte velden in.
    </div>

    <div class="flex justify-end gap-2">
      <Button
        data-testid="contract-edit-cancel"
        label="Annuleren"
        severity="secondary"
        text
        :disabled="saving"
        @click="onCancel"
      />
      <Button
        data-testid="contract-edit-save"
        label="Opslaan"
        severity="success"
        icon="pi pi-check"
        :disabled="!isDirty"
        :loading="saving"
        @click="onSave"
      />
    </div>
  </div>
</template>
